import { normalizeCustomTrackDefinition, type CustomTrackDefinition, type CustomTrackPoint } from "./customTrackSchema";

/** Legacy's usual 80-unit half-width becomes 10 metres. One fixed conversion
 * preserves relative authored widths; it never rescales a layout to fit. */
export const CUSTOM_TRACK_METRES_PER_UNIT = 0.125;
export interface CustomLayoutSample { x: number; y: number; z: number; distance: number }
export interface CustomLayoutBridge { underIndex: number; overIndex: number; half: number }
export interface CustomTrackLayout {
  definition: CustomTrackDefinition;
  /** Includes the exact closing sample at distance=length. */
  samples: readonly CustomLayoutSample[];
  length: number;
  halfWidth: number;
  bridges: readonly CustomLayoutBridge[];
  /** Effective cluster centres after avoiding crossover decks, in [0,1). */
  itemProgress: readonly number[];
}
export interface TrackLayoutGeometry {
  /** Prepared centreline in the source layout's coordinate units. */
  readonly points: readonly CustomTrackPoint[];
  readonly bridges: readonly CustomLayoutBridge[];
  /** Scaled 3D samples, including the exact closing sample. */
  readonly samples: readonly CustomLayoutSample[];
  readonly length: number;
}
const mod = (value: number, length: number) => ((value % length) + length) % length;
const separation = (a: number, b: number, n: number) => Math.min(mod(a - b, n), mod(b - a, n));
const distance = (a: CustomTrackPoint, b: CustomTrackPoint) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/** Legacy markers use sample-index progress, not v2's 3D arc distance.
 * Interpolate through the closing sample so markers on elevated or uneven
 * segments stay attached to their authored road position. */
export function customTrackProgressToDistance(layout: CustomTrackLayout, progress: number): number {
  if (!Number.isFinite(progress)) throw new Error("Custom track progress must be finite");
  const count = layout.samples.length - 1;
  const index = mod(progress, 1) * count;
  const lower = Math.floor(index), fraction = index - lower;
  const a = layout.samples[lower].distance, b = layout.samples[lower + 1].distance;
  return a + (b - a) * fraction;
}

function smooth(raw: readonly CustomTrackPoint[]): CustomTrackPoint[] {
  const points: CustomTrackPoint[] = [], n = raw.length;
  for (let i = 0; i < n; i++) {
    const p0 = raw[mod(i - 1, n)], p1 = raw[i], p2 = raw[(i + 1) % n], p3 = raw[(i + 2) % n];
    // Retain the legacy editor's spline sampling, before arc-length resampling.
    for (let t = 0; t < 1; t += 0.05) {
      const t2 = t * t, t3 = t2 * t;
      const axis = (k: 0 | 1) => 0.5 * (2 * p1[k] + (-p0[k] + p2[k]) * t
        + (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t2
        + (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * t3);
      points.push([axis(0), axis(1)]);
    }
  }
  return points;
}

function resample(points: readonly CustomTrackPoint[]): CustomTrackPoint[] {
  let total = 0;
  for (let i = 0; i < points.length; i++) total += distance(points[i], points[(i + 1) % points.length]);
  if (!Number.isFinite(total) || total < 1) throw new Error("Custom track centreline is degenerate");
  const count = Math.max(120, Math.min(2400, Math.round(total / 8)));
  // Port the legacy continuation arithmetic exactly, but work on a private
  // copy and reject a degenerate/incomplete walk rather than leaking NaNs.
  const remaining = points.map(p => [p[0], p[1]] as CustomTrackPoint);
  const result: CustomTrackPoint[] = [[points[0][0], points[0][1]]];
  const step = total / count;
  let segment = 0, carried = 0;
  while (result.length < count && segment <= points.length * 3) {
    const a = remaining[segment % points.length], b = remaining[(segment + 1) % points.length];
    const span = distance(a, b);
    if (span > 0 && carried + span >= step) {
      const t = (step - carried) / span;
      const point: CustomTrackPoint = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
      result.push(point); remaining[segment % points.length] = point; carried = 0;
    } else { carried += span; segment++; }
  }
  if (result.length !== count) throw new Error("Custom track resampling did not close");
  return result;
}

function placeStart(points: CustomTrackPoint[], width: number): CustomTrackPoint[] {
  const n = points.length, curvature: number[] = [], nearOther = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const a = points[mod(i - 6, n)], b = points[i], c = points[(i + 6) % n];
    let angle = Math.abs(Math.atan2(c[1] - b[1], c[0] - b[0]) - Math.atan2(b[1] - a[1], b[0] - a[0]));
    if (angle > Math.PI) angle = 2 * Math.PI - angle;
    curvature.push(angle);
  }
  for (let i = 0; i < n; i += 2) for (let j = 0; j < n; j += 2) {
    if (separation(i, j, n) <= 60) continue;
    if (distance(points[i], points[j]) < 2 * width) nearOther[i] = nearOther[(i + 1) % n] = 1;
  }
  let best = 0, bestScore = Infinity;
  for (let i = 0; i < n; i++) {
    let score = 0;
    for (let offset = -40; offset <= 14; offset++) {
      const k = mod(i + offset, n);
      score = Math.max(score, curvature[k] + (nearOther[k] ? 10 : 0));
    }
    if (score < bestScore) { best = i; bestScore = score; }
  }
  return points.slice(best).concat(points.slice(0, best));
}

function findBridges(points: readonly CustomTrackPoint[], width: number): CustomLayoutBridge[] {
  const bridges: CustomLayoutBridge[] = [], n = points.length;
  for (let i = 0; i < n; i += 2) for (let j = i + 60; j < n; j += 2) {
    if (separation(i, j, n) <= 60) continue;
    if (distance(points[i], points[j]) ** 2 < width * width * 0.4
      && !bridges.some(b => Math.abs(b.underIndex - i) < 40 && Math.abs(b.overIndex - j) < 40)) {
      bridges.push({ underIndex: i, overIndex: j, half: 16 });
    }
  }
  for (const bridge of bridges) {
    const nearLeg = (index: number) => {
      let nearest = Infinity;
      for (let offset = -60; offset <= 60; offset++) nearest = Math.min(nearest, distance(points[index], points[mod(bridge.underIndex + offset, n)]));
      return nearest;
    };
    let half = 0;
    while (half < 44 && (nearLeg(mod(bridge.overIndex + half, n)) < width * 1.9 || nearLeg(mod(bridge.overIndex - half, n)) < width * 1.9)) half++;
    bridge.half = Math.min(44, half + 4);
  }
  return bridges;
}

export function trackLayoutElevation(index: number, n: number, bridges: readonly CustomLayoutBridge[], dips: readonly number[]): number {
  const hump = (centre: number, half: number) => {
    const t = Math.min(1, separation(index, centre, n) / half);
    return 1 - t * t * (3 - 2 * t);
  };
  let height = 0, depth = 0;
  for (const b of bridges) height = Math.max(height, 54 * hump(b.overIndex, Math.max(8, b.half)));
  for (const progress of dips) depth = Math.max(depth, 6 * hump(Math.round(progress * n) % n, 24));
  return (height - depth) * CUSTOM_TRACK_METRES_PER_UNIT;
}

/**
 * Shared source-layout preparation used by both editor-authored custom
 * tracks and the detached v1 built-in adapter.  The mode is explicit because
 * v1 built-ins use `smooth(raw, .05)` directly while the editor path adds the
 * legacy arc-length resampling pass.  Keeping the switch here prevents a
 * future adapter from accidentally treating a built-in as a custom document.
 *
 * `includeElevationInDistance` retains the existing custom behaviour.  The
 * v1 source length is planar (its `pts` are 2D), so the legacy adapter leaves
 * bridge elevation out of `length` while still exposing it in each sample's
 * y coordinate.
 */
export function prepareTrackLayoutGeometry(
  raw: readonly CustomTrackPoint[],
  width: number,
  options: { readonly resampleByArc: boolean; readonly dips?: readonly number[]; readonly includeElevationInDistance: boolean },
): TrackLayoutGeometry {
  const smoothed = smooth(raw);
  const sampled = options.resampleByArc ? resample(smoothed) : smoothed;
  const points = placeStart(sampled, width);
  const bridges = findBridges(points, width);
  const dips = options.dips ?? [];
  const samples: CustomLayoutSample[] = [];
  let length = 0;
  for (let i = 0; i <= points.length; i += 1) {
    const index = i % points.length;
    const point = {
      x: points[index][0] * CUSTOM_TRACK_METRES_PER_UNIT,
      y: trackLayoutElevation(index, points.length, bridges, dips),
      z: points[index][1] * CUSTOM_TRACK_METRES_PER_UNIT,
      distance: length,
    };
    if (i) {
      const previous = samples[i - 1];
      const dy = options.includeElevationInDistance ? point.y - previous.y : 0;
      length += Math.hypot(point.x - previous.x, dy, point.z - previous.z);
      point.distance = length;
    }
    samples.push(point);
  }
  return { points, bridges, samples, length };
}

/** Shared deterministic layout preparation for future editor preview, server
 * raceability checks and game rendering. This does not register a course or
 * grant permission to save it. No built-in shortcut/signature zone is invented. */
export function buildCustomTrackLayout(input: unknown): CustomTrackLayout {
  const definition = normalizeCustomTrackDefinition(input);
  const geometry = prepareTrackLayoutGeometry(definition.raw, definition.width, {
    resampleByArc: true,
    dips: definition.dips ?? [],
    includeElevationInDistance: true,
  });
  const { points, bridges, samples, length } = geometry;
  const n = points.length;
  const inBridge = (i: number) => bridges.some(b => separation(i, b.overIndex, n) < b.half + 12 || separation(i, b.underIndex, n) < b.half + 12);
  const positions = definition.items?.length ? definition.items : Array.from({ length: 6 }, (_, i) => i / 6);
  const itemProgress = positions.map(progress => {
    let index = Math.floor(mod(progress, 1) * n), attempts = 0;
    while (inBridge(index) && attempts++ < n) index = (index + 4) % n;
    return index / n;
  });
  return { definition, samples, length, halfWidth: definition.width * CUSTOM_TRACK_METRES_PER_UNIT, bridges, itemProgress };
}
